export interface Cohort {
  slug: string;
  certificationName: string;
  level: string | number;
  startDate: string;
  endDate?: string;
  schedule: string;
  price?: number;
  currency?: string;
  enrollmentDeadline?: string;
  format: string;
  monthYear?: string;
  certificationUrl?: string;
  description?: string;
  sessionDates?: string[];
  spotsAvailable?: number;
  seatsAvailable?: number;
  seatsTotal?: number;
  status?: CohortStatus;
  faculty?: string;
  icfCredits?: string;
  id?: string;
  applicationDeadline?: string;
  displayDate?: string;
}

export type CohortStatus = 'Open' | 'In progress' | 'Finished';

export function getCurrentSastDate(searchParams?: URLSearchParams | string | null): Date {
  if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
    let params: URLSearchParams | null = null;
    if (typeof searchParams === 'string') {
      params = new URLSearchParams(searchParams);
    } else if (searchParams instanceof URLSearchParams) {
      params = searchParams;
    } else if (typeof window !== 'undefined') {
      params = new URLSearchParams(window.location.search);
    }
    const asOf = params?.get('asOf');
    if (asOf && /^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
      const [y, m, d] = asOf.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
  }
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(now);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '2026', 10);
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '11', 10);
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '1', 10);
  return new Date(year, month - 1, day);
}

export function getCohortStatus(cohort: { startDate: string; endDate?: string }, searchParams?: URLSearchParams | string | null): CohortStatus {
  const currentDate = getCurrentSastDate(searchParams);
  const currentTimestamp = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
  
  const [startYear, startMonth, startDay] = cohort.startDate.split('-').map(Number);
  const startTimestamp = new Date(startYear, startMonth - 1, startDay).getTime();
  
  if (!cohort.endDate) {
    if (currentTimestamp < startTimestamp) {
      return 'Open';
    } else {
      return 'In progress';
    }
  }

  const [endYear, endMonth, endDay] = cohort.endDate.split('-').map(Number);
  const endTimestamp = new Date(endYear, endMonth - 1, endDay).getTime();
  
  if (currentTimestamp < startTimestamp) {
    return 'Open';
  } else if (currentTimestamp >= startTimestamp && currentTimestamp <= endTimestamp) {
    return 'In progress';
  } else {
    return 'Finished';
  }
}

export const cohorts: Cohort[] = [
  {
    id: 'practitioner-nov-2026',
    slug: 'practitioner-nov-2026',
    certificationName: 'FIVE LENS Enneagram Practitioner Certification',
    level: 1,
    startDate: '2026-11-03',
    endDate: '2026-11-19',
    displayDate: '3 November 2026',
    schedule: 'Tue 3 Nov, Thu 5 Nov, Tue 10 Nov, Thu 12 Nov, Tue 17 Nov, Thu 19 Nov 2026 — 6 x 3-hour live Zoom',
    format: 'Blended virtual: 8–10 hours self-paced online + 6 live Zoom sessions + 1-on-1 supervised debrief',
    monthYear: 'November 2026',
    certificationUrl: '/practitioner-certification',
    description: 'ICF-accredited Level 1 certification programme teaching coaches, HR professionals, and leaders to debrief FIVE LENS reports with clients. Covers the Enneagram plus four additional lenses of psychological insight.',
    sessionDates: [
      'Tue 3 Nov 2026',
      'Thu 5 Nov 2026',
      'Tue 10 Nov 2026',
      'Thu 12 Nov 2026',
      'Tue 17 Nov 2026',
      'Thu 19 Nov 2026'
    ],
    spotsAvailable: 15,
    faculty: 'FIVE LENS Master Practitioners & Senior Faculty',
    icfCredits: '23.75 ICF CCE Units'
  },
  {
    id: 'practitioner-dec-2026',
    slug: 'practitioner-dec-2026',
    certificationName: 'FIVE LENS Enneagram Practitioner Certification',
    level: 1,
    startDate: '2026-12-01',
    displayDate: 'Tuesday 1 December 2026',
    schedule: '6 x 3-hour live Zoom sessions',
    format: 'Blended virtual: 8–10 hours self-paced online + 6 live Zoom sessions + 1-on-1 supervised debrief',
    monthYear: 'December 2026',
    certificationUrl: '/practitioner-certification',
    description: 'ICF-accredited Level 1 certification programme teaching coaches, HR professionals, and leaders to debrief FIVE LENS reports with clients. Covers the Enneagram plus four additional lenses of psychological insight.',
    spotsAvailable: 15,
    faculty: 'FIVE LENS Master Practitioners & Senior Faculty',
    icfCredits: '23.75 ICF CCE Units'
  },
  {
    id: 'practitioner-jan-2027',
    slug: 'practitioner-jan-2027',
    certificationName: 'FIVE LENS Enneagram Practitioner Certification',
    level: 1,
    startDate: '2027-01-15',
    displayDate: 'Friday 15 January 2027',
    schedule: '6 x 3-hour live Zoom sessions',
    format: 'Blended virtual: 8–10 hours self-paced online + 6 live Zoom sessions + 1-on-1 supervised debrief',
    monthYear: 'January 2027',
    certificationUrl: '/practitioner-certification',
    description: 'ICF-accredited Level 1 certification programme teaching coaches, HR professionals, and leaders to debrief FIVE LENS reports with clients. Covers the Enneagram plus four additional lenses of psychological insight.',
    spotsAvailable: 15,
    faculty: 'FIVE LENS Master Practitioners & Senior Faculty',
    icfCredits: '23.75 ICF CCE Units'
  },
  {
    id: 'team-facilitator-jan-2027',
    slug: 'team-facilitator-jan-2027',
    certificationName: 'FIVE LENS Team Facilitator Certification',
    level: 2,
    startDate: '2027-01-12',
    endDate: '2027-02-06',
    displayDate: '12 January 2027',
    schedule: '4 x 2-3 hour live Zoom sessions over 4 weeks',
    format: 'Blended virtual: 8–10 hours pre-course online + 4 live Zoom sessions + case studies',
    monthYear: 'January 2027',
    certificationUrl: '/certification/team-facilitator',
    description: 'Level 2 certification programme for facilitating team-building and leadership sessions using FIVE LENS modules on trust, feedback and collaboration.',
    sessionDates: [
      'Tue 12 Jan 2027',
      'Thu 21 Jan 2027',
      'Thu 28 Jan 2027',
      'Thu 4 Feb 2027'
    ],
    spotsAvailable: 15,
    faculty: 'FIVE LENS Master Facilitators',
    icfCredits: 'ICF CCE accreditation coming in 2027'
  }
];

export interface CohortDetail extends Cohort {}

export const cohortDetails: CohortDetail[] = cohorts;

export function getCohortBySlug(slug: string): Cohort | undefined {
  return cohorts.find((c) => c.slug === slug);
}

export function getNextIntake(level: 1 | 2, searchParams?: URLSearchParams | string | null): Cohort | null {
  const openCohorts = cohorts.filter((c) => {
    const lvl = typeof c.level === 'number' ? c.level : parseInt(c.level.toString().replace('Level ', ''), 10);
    if (lvl !== level) return false;
    return getCohortStatus(c, searchParams) === 'Open';
  });
  return openCohorts[0] || null;
}
